'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

type SpinnerVariant = 'spinner' | 'dots' | 'pulse' | 'skeleton';
type SpinnerSize = 'sm' | 'md' | 'lg';

interface LoadingSpinnerProps {
  variant?: SpinnerVariant;
  size?: SpinnerSize;
  label?: string;
  fullPage?: boolean;
  inline?: boolean;
  className?: string;
}

const sizeMap: Record<SpinnerSize, { container: string; ring: string; dot: string }> = {
  sm: { container: 'h-4 w-4', ring: 'h-4 w-4 border-2', dot: 'h-1.5 w-1.5' },
  md: { container: 'h-8 w-8', ring: 'h-8 w-8 border-[3px]', dot: 'h-2.5 w-2.5' },
  lg: { container: 'h-14 w-14', ring: 'h-14 w-14 border-4', dot: 'h-3.5 w-3.5' },
};

const Spinner: React.FC<{ size: SpinnerSize }> = ({ size }) => (
  <div
    className={cn(
      sizeMap[size].ring,
      'animate-spin rounded-full border-indigo-500 border-t-transparent'
    )}
  />
);

const Dots: React.FC<{ size: SpinnerSize }> = ({ size }) => (
  <div className="flex items-center gap-1.5">
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className={cn(sizeMap[size].dot, 'rounded-full bg-primary-900')}
        animate={{ y: [0, -8, 0] }}
        transition={{
          duration: 0.6,
          repeat: Infinity,
          delay: i * 0.15,
          ease: 'easeInOut',
        }}
      />
    ))}
  </div>
);

const Pulse: React.FC<{ size: SpinnerSize }> = ({ size }) => (
  <motion.div
    className={cn(sizeMap[size].container, 'rounded-full bg-primary-900')}
    animate={{ scale: [1, 1.4, 1], opacity: [0.7, 0.2, 0.7] }}
    transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
  />
);

const Skeleton: React.FC<{ size: SpinnerSize }> = ({ size }) => {
  const heightClass = size === 'sm' ? 'h-4' : size === 'md' ? 'h-8' : 'h-14';
  return (
    <div className={cn('w-full overflow-hidden rounded-md bg-zinc-800', heightClass)}>
      <motion.div
        className="h-full w-1/2 bg-gradient-to-r from-transparent via-zinc-700/50 to-transparent"
        animate={{ x: ['-100%', '300%'] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
};

const variantComponents: Record<SpinnerVariant, React.FC<{ size: SpinnerSize }>> = {
  spinner: Spinner,
  dots: Dots,
  pulse: Pulse,
  skeleton: Skeleton,
};

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  variant = 'spinner',
  size = 'md',
  label,
  fullPage = false,
  inline = false,
  className,
}) => {
  const { t } = useTranslation();
  const VariantComponent = variantComponents[variant];
  const accessibleLabel = label || t('loading.label', 'Loading…');

  const content = (
    <div
      role="status"
      aria-label={accessibleLabel}
      className={cn(
        'flex items-center gap-3',
        !inline && 'flex-col justify-center',
        className
      )}
    >
      <VariantComponent size={size} />
      {label && (
        <span
          className={cn(
            'text-zinc-400',
            size === 'sm' && 'text-xs',
            size === 'md' && 'text-sm',
            size === 'lg' && 'text-base'
          )}
        >
          {label}
        </span>
      )}
      <span className="sr-only">{accessibleLabel}</span>
    </div>
  );

  if (fullPage) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-zinc-950/60 backdrop-blur-sm">
        {content}
      </div>
    );
  }

  return content;
};

export { LoadingSpinner };
export default LoadingSpinner;
